import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from '../../../environments/environment';
import { Observable, catchError, combineLatest, forkJoin, map, of, switchMap, take, tap } from "rxjs";
import { IBuilding } from "../../shared/models/be/building";
import { IUnit } from "../../shared/models/be/unit";
import { IRoom } from "../../shared/models/be/room";
import { IBed } from "../../shared/models/be/bed";
import { IOrganization } from "../../shared/models/be/organization";
import { RequestsService } from "./utils/requests.service";
import { LoggerService } from "./utils/logger.service";

export interface FacilityWriteContext { customerId: number; organizationId: string; }
export interface FacilityCreatedResponse { message: string; id: string | null; }
export interface FacilityBedsCreatedResponse { message: string; beds: { id: string }[]; }
export interface FacilityDeleteResponse { message: string; results?: Record<string, string>; }

@Injectable({
    providedIn: 'any'
})
export class FacilitiesService {

    constructor(
        private _http: HttpClient,
        private requestsService: RequestsService,
        private loggerService: LoggerService
    ) { }

    /* BUNDLE */

    /**
     * @description Funzione che dato l'id del reparto/appartamento in oggetto torna il reparto e le unità ad esso correlate
     * @param unitId Id del reparto/appartamento
     * @param organizationId Id dell'organizzazione
     * @param customerId Id del cliente, necessario al fine di poter effettuare le chiamate
     * @returns Reparto/appartamento e le unità ad esso correlate
     */
    public getWardByIdWithRoomsAndBeds$(unitId: string): Observable<{ ward: IUnit, rooms: IRoom[] }> {
        return combineLatest([
            this.getUnitById$(unitId),
            this.getRooms$(unitId).pipe(
                switchMap(rooms => rooms.length
                    ? forkJoin(rooms.map(room => this.getBeds$(room.id!).pipe(map(beds => ({ ...room, beds })))))
                    : of([])),
                map(rooms => ({ rooms }))
            )
        ]).pipe(
            take(1),
            map(([ward, rooms]) => ({
                ward,
                rooms: rooms.rooms
            })),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getServiceStatus$(): Observable<boolean> {
        return this._http.get<{ message: string }>(`${environment.apiEndpoint}/facilities/ping`).pipe(
            take(1),
            map((val: { message: string }) => !!val.message),
            catchError(err => {
                this.loggerService.error('Error on .../ping')
                return of(false);
            })
        );
    }

    /*  ORGANIZATION  */

    public getOrganization$(customerId?: number) {
        const customerIdQuery = customerId ? `?customerId=${customerId}` : '';
        return this._http.get<{ organizations: IOrganization[] }>(
            `${environment.apiEndpoint}/facilities/organizations${customerIdQuery}`
        ).pipe(
            take(1),
            map(data => data.organizations),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getOrganizationById$(organizationId?: string) {
        return this._http.get<IOrganization>(
            `${environment.apiEndpoint}/facilities/organizations/${organizationId}`
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public postOrganization$(organization: IOrganization) {
        return this._http.post<{ id: string }>(
            `${environment.apiEndpoint}/facilities/organizations?customerId=${organization.customerId}`,
            { organizations: [organization] }
        ).pipe(
            take(1),
            /* map(data => data), */
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        );
    }

    public putOrganization$(organization: IOrganization) {
        return this._http.put(
            `${environment.apiEndpoint}/facilities/organizations/${organization.id}?customerId=${organization.customerId}`,
            organization
        ).pipe(
            take(1),
            tap((data) => {
                if (organization.id == this.requestsService.getCurrentUser()?.organization?.id) {
                    this.requestsService.saveCurrentUser({
                        ...this.requestsService.getCurrentUser()!,
                        organization: {
                            id: organization.id,
                            businessName: organization.name
                        }
                    }
                    )
                }
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        );
    }

    public deleteOrganizations$(orgIds: string[], customerId: string) {
        return this._http.delete(
            `${environment.apiEndpoint}/facilities/organizations?customerId=${customerId}`,
            {
                body: {
                    customerId,
                    orgIds
                }
            }
        ).pipe(
            take(1),
            map(data => data),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        );
    }

    /*  BUILDING  */

    /**
     * @description Recupero le facilities di tipo "Edificio"
     * @param retrieveFullStructure Opzionale, se true mi torna TUTTA la alberature dell'edificio, quindi avrò pure reparti stanze e letti
     * @param orgId Opzionale, se valorizzato lo imposta nei queryParams altrimenti ci mette quello trovato nel localStorage (se presente)
     */
    public getBuildings$(orgId?: string, retrieveFullStructure: boolean = false) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.get<{ buildings: IBuilding[] }>(
            `${environment.apiEndpoint}/facilities/buildings?organizationId=${orgId || currentUser?.organization?.id}&fullStructure=${retrieveFullStructure}`
        ).pipe(
            take(1),
            map(data => data.buildings),
            tap((buildings) => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getBuilding$(buildingId: string, retrieveFullStructure: boolean = false) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.get<IBuilding>(
            `${environment.apiEndpoint}/facilities/buildings/${buildingId}?customerId=${currentUser?.customer?.id}&organizationId=${currentUser?.organization?.id}&fullStructure=${retrieveFullStructure}`
        ).pipe(
            take(1),
            tap((building) => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public postBuildings$(building: IBuilding) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.post<{ buildings: IBuilding }>(
            `${environment.apiEndpoint}/facilities/buildings`,
            {
                buildings: [{
                    ...building,
                    customerId: Number(currentUser?.customer?.id),
                    organizationId: currentUser?.organization?.id,
                }]
            }
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public putBuildings$(building: IBuilding) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.put(
            `${environment.apiEndpoint}/facilities/buildings`,
            {
                ...building,
                customerId: currentUser?.customer?.id,
                organizationId: currentUser?.organization?.id
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        );
    }

    public deleteBuilding$(buildingId: string) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.delete(
            `${environment.apiEndpoint}/facilities/buildings`,
            {
                body: {
                    buildingIds: [buildingId],
                    organizationId: currentUser?.organization?.id
                }
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        );
    }

    /* UNITS */

    public getUnits$(buildingId?: string) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.get<{ units: IUnit[] }>(
            `${environment.apiEndpoint}/facilities/units?buildingId=${buildingId || currentUser?.building?.id}`
        ).pipe(
            take(1),
            map(data => data.units),
            tap((units) => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getUnitById$(unitId: string) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.get<IUnit>(
            `${environment.apiEndpoint}/facilities/units/${unitId}?customerId=${currentUser?.customer?.id}&organizationId=${currentUser?.organization?.id}`
        ).pipe(
            take(1),
            tap((units) => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public postUnit$(unit: IUnit, context?: FacilityWriteContext) {
        const currentUser = this.requestsService.getCurrentUser();
        const scope = context || { customerId: currentUser?.customer?.id, organizationId: currentUser?.organization?.id };
        return this._http.post<FacilityCreatedResponse>(
            `${environment.apiEndpoint}/facilities/units`,
            {
                units: [{
                    ...unit,
                    customerId: scope.customerId,
                    organizationId: scope.organizationId
                }]
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => { throw err; })
        )
    }

    public putUnit$(unit: IUnit, context?: FacilityWriteContext) {
        const currentUser = this.requestsService.getCurrentUser();
        const scope = context || { customerId: currentUser?.customer?.id, organizationId: currentUser?.organization?.id };
        return this._http.put<{ message: string; units?: IUnit[] }>(
            `${environment.apiEndpoint}/facilities/units`,
            {
                units: [{
                    ...unit,
                    customerId: scope.customerId,
                    organizationId: scope.organizationId
                }],
                organizationId: scope.organizationId
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => { throw err; })
        )
    }

    public deleteUnit$(unitId: string) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.delete(
            `${environment.apiEndpoint}/facilities/units`,
            {
                body: {
                    units: [unitId],
                    organizationId: currentUser?.organization?.id
                }
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        );
    }

    /* ROOMS */

    public getRooms$(unitId: string, orgId?: string) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.get<{ rooms: IRoom[] }>(
            `${environment.apiEndpoint}/facilities/rooms?unitId=${unitId}&organizationId=${orgId || currentUser?.organization?.id}`
        ).pipe(
            take(1),
            map(data => data.rooms),
            tap((units) => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public postRoom$(room: IRoom, context?: FacilityWriteContext) {
        const currentUser = this.requestsService.getCurrentUser();
        const scope = context || { customerId: currentUser?.customer?.id, organizationId: currentUser?.organization?.id };
        return this._http.post<FacilityCreatedResponse>(
            `${environment.apiEndpoint}/facilities/rooms`,
            {
                rooms: [{
                    ...room,
                    customerId: scope.customerId,
                    organizationId: scope.organizationId
                }]
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => { throw err; })
        )
    }

    public putRoom$(room: IRoom, context?: FacilityWriteContext) {
        const currentUser = this.requestsService.getCurrentUser();
        const scope = context || { customerId: currentUser?.customer?.id, organizationId: currentUser?.organization?.id };
        return this._http.put<{ message: string }>(
            `${environment.apiEndpoint}/facilities/rooms`,
            {
                organizationId: scope.organizationId,
                rooms: [{
                    ...room,
                    customerId: scope.customerId,
                    organizationId: scope.organizationId
                }]
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => { throw err; })
        )
    }

    public deleteRoom$(roomId: string, context?: FacilityWriteContext) {
        const currentUser = this.requestsService.getCurrentUser();
        const scope = context || { customerId: currentUser?.customer?.id, organizationId: currentUser?.organization?.id };
        return this._http.delete<FacilityDeleteResponse>(
            `${environment.apiEndpoint}/facilities/rooms`,
            {
                body: {
                    roomIds: [roomId],
                    organizationId: scope.organizationId
                }
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => { throw err; })
        );
    }

    /* BEDS */

    public getBeds$(roomId: string, context?: FacilityWriteContext): Observable<IBed[]> {
        const currentUser = this.requestsService.getCurrentUser();
        const scope = context || { customerId: currentUser?.customer?.id, organizationId: currentUser?.organization?.id };
        return this._http.get<{ beds: IBed[] }>(
            `${environment.apiEndpoint}/facilities/beds?roomId=${roomId}&customerId=${scope.customerId}&organizationId=${scope.organizationId}`
        ).pipe(
            take(1),
            map(data => data.beds),
            tap((units) => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public postBed$(bed: IBed, context?: FacilityWriteContext) {
        const currentUser = this.requestsService.getCurrentUser();
        const scope = context || { customerId: currentUser?.customer?.id, organizationId: currentUser?.organization?.id };
        return this._http.post<FacilityBedsCreatedResponse>(
            `${environment.apiEndpoint}/facilities/beds`,
            {
                beds: [{
                    ...bed,
                    customerId: scope.customerId,
                    organizationId: scope.organizationId
                }]
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => { throw err; })
        )
    }

    public putBed$(bed: IBed, context?: FacilityWriteContext) {
        const currentUser = this.requestsService.getCurrentUser();
        const scope = context || { customerId: currentUser?.customer?.id, organizationId: currentUser?.organization?.id };
        return this._http.put<{ message: string }>(
            `${environment.apiEndpoint}/facilities/beds/${bed.id}`,
            {
                ...bed,
                customerId: scope.customerId,
                organizationId: scope.organizationId
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => { throw err; })
        )
    }

    public deleteBed$(bedId: string, context?: FacilityWriteContext) {
        const currentUser = this.requestsService.getCurrentUser();
        const scope = context || { customerId: currentUser?.customer?.id, organizationId: currentUser?.organization?.id };
        return this._http.delete<FacilityDeleteResponse>(
            `${environment.apiEndpoint}/facilities/beds/${encodeURIComponent(scope.organizationId || '')}`,
            {
                body: {
                    beds: [bedId]
                }
            }
        ).pipe(
            take(1),
            tap(() => {
                //this.store.dispatch(loadCustomersSuccess({customers}))
            }),
            catchError(err => { throw err; })
        );
    }
}
